import React, { createContext, useCallback, useContext, useEffect, useState, useMemo } from "react";
import { trackAddToCart, trackMetaAddToCart } from "../analytics";
import { useSiteConfig, saleUnit } from "./SiteConfigContext";
import { validatePromoApi, apiErrorMessage } from "../api";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "venus_cart_v2";
const PROMO_KEY = "venus_promo_v1";
const PENDING_PROMO_KEY = "venus_promo_pending";

export const CartProvider = ({ children }) => {
  const { discount } = useSiteConfig();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, openDrawer = true) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          image: (product.images && product.images[0]) || product.image,
          qty,
        },
      ];
    });
    try { trackAddToCart(product, qty); trackMetaAddToCart(product, qty, saleUnit(product.price, discount)); } catch (e) { /* no-op */ }
    if (openDrawer) setOpen(true);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) => {
    if (qty <= 0) return removeItem(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };
  const clearCart = () => { setItems([]); setPromo(null); };

  const [promo, setPromo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROMO_KEY)) || null; } catch (e) { return null; }
  });
  const [promoError, setPromoError] = useState("");

  useEffect(() => {
    if (promo) localStorage.setItem(PROMO_KEY, JSON.stringify(promo));
    else localStorage.removeItem(PROMO_KEY);
  }, [promo]);

  const applyPromo = useCallback(async (code) => {
    setPromoError("");
    try {
      const r = await validatePromoApi(code, items);
      setPromo({ code: r.code, discount: r.discount });
      return true;
    } catch (err) {
      setPromoError(apiErrorMessage(err));
      return false;
    }
  }, [items]);
  const removePromo = () => { setPromo(null); setPromoError(""); };

  // Code captured from a shared ?promo= link, applied once the bag has items
  const [pendingCode, setPendingCode] = useState(() => localStorage.getItem(PENDING_PROMO_KEY) || "");
  const savePendingCode = (code) => {
    localStorage.setItem(PENDING_PROMO_KEY, code);
    setPendingCode(code);
    setPromoError("");
  };
  useEffect(() => {
    if (!pendingCode || items.length === 0) return;
    const code = pendingCode;
    localStorage.removeItem(PENDING_PROMO_KEY);
    setPendingCode("");
    if (promo?.code === code) return;
    validatePromoApi(code, items)
      .then((r) => { setPromo({ code: r.code, discount: r.discount }); toast.success(`Promo code ${r.code} applied`); })
      .catch((err) => toast.error(`Promo code ${code} couldn't be applied`, { description: apiErrorMessage(err) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCode, items.length]);

  // Re-check the applied code whenever the bag or the sitewide sale changes
  const promoCode = promo?.code;
  useEffect(() => {
    if (!promoCode) return;
    if (items.length === 0) { setPromo(null); return; }
    const t = setTimeout(() => {
      validatePromoApi(promoCode, items)
        .then((r) => setPromo({ code: r.code, discount: r.discount }))
        .catch((err) => {
          if (err?.response?.status !== 400) return; // keep the code on network/rate-limit hiccups
          setPromo(null); setPromoError(apiErrorMessage(err));
        });
    }, 300);
    return () => clearTimeout(t);
  }, [items, discount, promoCode]);

  const { count, subtotal, originalSubtotal, savings } = useMemo(() => {
    const c = items.reduce((s, i) => s + i.qty, 0);
    const orig = items.reduce((s, i) => s + i.qty * i.price, 0);
    // Per-unit rounding matches the backend's apply_discount so cart totals
    // always equal what the order endpoint will charge.
    const t = items.reduce((s, i) => s + i.qty * saleUnit(i.price, discount), 0);
    return {
      count: c,
      subtotal: Math.round(t * 100) / 100,
      originalSubtotal: Math.round(orig * 100) / 100,
      savings: Math.round((orig - t) * 100) / 100,
    };
  }, [items, discount]);

  const promoDiscount = promo ? Math.min(promo.discount, subtotal) : 0;

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQty, clearCart, count, subtotal, originalSubtotal, savings, discount, open, setOpen,
      promo, promoDiscount, promoError, applyPromo, removePromo, savePendingCode }),
    [items, count, subtotal, originalSubtotal, savings, discount, open, promo, promoDiscount, promoError, applyPromo]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
